import { useEffect } from "react"
import { ssoApi } from "../../api/lensService";

export const CallBack = () => {
    // Handle SSO callback with ?id= query parameter
    const handleSSOCallback = async (id) => {
        if (!id) {
            document.location.href = "/";
            return;
        }
        const result = await ssoApi.get(`api/auth/getToken?id=${id}`);
        localStorage.setItem('TOKEN', result?.data?.data?.accessToken);
        localStorage.setItem("user", JSON.stringify({ ...result?.data?.data, remember: result?.data?.data?.remember }));
        document.location.href = "/dashboard";
    }

    // Handle Cognito callback with #access_token= hash fragment
    const handleCognitoCallback = (hashParams) => {

        const accessToken = hashParams.get('access_token');
        const idToken = hashParams.get('id_token');

        // Prefer the ID token: email / given_name / family_name exist only there, and the
        // backend needs them to key users on their email rather than a Cognito UUID.
        //
        // Fall back to the access token rather than blocking login. The backend resolves
        // users from the Cognito sub, which both token types carry, so a UUID-only token
        // still authenticates correctly - the user is just keyed on their UUID, which is
        // the pre-existing behaviour. Warn loudly so this is visible rather than silent:
        // a missing id_token means cognito.scope is missing openid.
        const bearerToken = idToken || accessToken;

        if (!bearerToken) {
            console.error('No id_token or access_token in Cognito callback');
            document.location.href = "/?error=missing_token";
            return;
        }

        if (!idToken) {
            console.warn(
                'No id_token in Cognito callback, falling back to the access token. Users ' +
                'will be keyed on their Cognito UUID instead of their email address. Add ' +
                'openid (and profile) to cognito.scope to fix this.'
            );
        }

        // Parse whichever token we are sending, so the values here always match what the
        // backend sees. Groups and sub are on both; email only on the ID token.
        let userData = {};
        try {
            const payload = JSON.parse(atob(bearerToken.split('.')[1]));

            // Check both cognito:groups (array) and custom:groups (comma-separated string)
            const cognitoGroups = payload['cognito:groups'] || [];
            const customGroups = payload['custom:groups'] || '';

            // Combine both sources into a single array
            const userGroupList = [
                ...(Array.isArray(cognitoGroups) ? cognitoGroups : []),
                ...customGroups.split(',').map(g => g.trim()).filter(g => g)
            ];

            // Map Cognito groups to roles and access type
            const adminGroups = ['APP_VK00_9DD0_TEKVISION_ADMINS', 'Admins'];
            const isAdmin = userGroupList.some(g => adminGroups.includes(g));
            const roles = isAdmin ? ['ROLE_ADMIN'] : ['ROLE_USER'];
            const accessType = isAdmin ? 'read-write' : 'read-only';

            userData = {
                // Prefer email so the frontend's notion of the current user matches the
                // identifier the backend keys rows on. Falls through the same order the
                // backend uses: email, then the ID token's cognito:username, then the
                // access token's username, then the sub.
                username: payload.email || payload['cognito:username'] || payload.username || payload.sub,
                email: payload.email,
                groups: userGroupList.join(','),
                sub: payload.sub,
                roles: roles,
                accessType: accessType
            };
        } catch (e) {
            console.error('Failed to parse id_token', e);
        }

        localStorage.setItem('TOKEN', bearerToken);
        localStorage.setItem("user", JSON.stringify({ ...userData, remember: false }));
        document.location.href = "/dashboard";
    }

    useEffect(() => {
        const hash = window.location.hash;
        const search = window.location.search;

        // Check for Cognito implicit grant. Match on either token so the callback is
        // still routed here when the pool is configured to return id_token only.
        if (hash && (hash.includes('id_token') || hash.includes('access_token'))) {
            const hashParams = new URLSearchParams(hash.substring(1));
            handleCognitoCallback(hashParams);
        }
        // Check for SSO callback (query parameter with id)
        else if (search && search.includes('id=')) {
            const id = search.replace('?id=', '');
            handleSSOCallback(id);
        }
        // No valid callback params
        else {
            console.error('No valid callback parameters found');
            document.location.href = "/";
        }
    }, [])

    return null;
}